import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BrandImportJobView } from '@mg/shared';
import { actionErrorText, api } from '../../../lib/api';
import {
  brandKeys,
  IMPORT_POLL_CEILING_MS,
  isImportActive,
  refreshAfterImportEnd,
  refreshAfterImportStart,
  useBrandImportJob,
  useLatestBrandImport,
  WIZARD_IMPORT_POLL_MS,
} from '../../brand/queries';
import { IMPORT_BANNER_COPY } from '../wizardCopy';

export interface ImportJobState {
  /** The workspace's import the wizard follows: the one started here, else the latest. Null: none yet. */
  job: BrandImportJobView | null;
  /** Queued, parsing or extracting. */
  active: boolean;
  /** Still running after the 13-minute client ceiling: polling stopped; the banner offers Try again. */
  timedOut: boolean;
  retrying: boolean;
  retryError: string | null;
  /** An upload started this job: follow it from now on. */
  started: (jobId: string) => void;
  /** Try again (after an AI failure, or past the ceiling): the same job runs again. */
  retry: () => Promise<void>;
}

/**
 * The import Brand Setup follows (web.md §5.4; final-design §2.14): the workspace's latest job when the
 * wizard opens, or the one an upload just started. It is polled every 2 s while it runs, for at most 13
 * minutes. When it ends, the latest job and the brand view are read again (refreshAfterImportEnd), so the
 * Edit page's sources list is current. Only onboarding imports count: a re-read (Release 4) is not one.
 */
export function useImportJob(tenantId: string, { enabled }: { enabled: boolean }): ImportJobState {
  const queryClient = useQueryClient();
  const latest = useLatestBrandImport(enabled ? tenantId : null);
  const latestJob = latest.data?.job?.purpose === 'onboarding' ? latest.data.job : null;
  const [startedId, setStartedId] = useState<string | null>(null);
  const jobId = startedId ?? latestJob?.id ?? null;

  // Each start or retry begins a new window of IMPORT_POLL_CEILING_MS (`epoch`).
  const [epoch, setEpoch] = useState(0);
  const [timedOutAt, setTimedOutAt] = useState<string | null>(null);
  const windowKey = jobId === null ? null : `${jobId}:${epoch}`;
  const timedOut = windowKey !== null && timedOutAt === windowKey;

  const jobQuery = useBrandImportJob(enabled ? tenantId : null, jobId, {
    activePollMs: timedOut ? false : WIZARD_IMPORT_POLL_MS,
  });
  const job = jobQuery.data ?? (latestJob && latestJob.id === jobId ? latestJob : null);
  const active = isImportActive(job);

  useEffect(() => {
    if (windowKey === null || !active) return;
    const timer = setTimeout(() => setTimedOutAt(windowKey), IMPORT_POLL_CEILING_MS);
    return () => clearTimeout(timer);
  }, [windowKey, active]);

  // A job seen running that has now ended: read the latest job and the brand view again.
  const seen = useRef<{ id: string; active: boolean } | null>(null);
  useEffect(() => {
    if (!job) return;
    const before = seen.current;
    seen.current = { id: job.id, active };
    if (before && before.id === job.id && before.active && !active) void refreshAfterImportEnd(queryClient, tenantId);
  }, [job, active, queryClient, tenantId]);

  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const follow = (id: string) => {
    setStartedId(id);
    setEpoch((n) => n + 1);
    setRetryError(null);
    void refreshAfterImportStart(queryClient, tenantId);
  };

  const retry = async () => {
    if (!job || retrying) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const { jobId: id } = await api.retryBrandImport(tenantId, job.id);
      follow(id);
      // Read it now: it is queued again, so polling starts over.
      await queryClient.invalidateQueries({ queryKey: brandKeys.importJob(tenantId, id) });
    } catch (err) {
      setRetryError(actionErrorText(err, IMPORT_BANNER_COPY.retryFailed));
    } finally {
      setRetrying(false);
    }
  };

  return { job, active, timedOut, retrying, retryError, started: follow, retry };
}
